import React from 'react';

export type ToastKind = 'success' | 'warning' | 'error' | 'info';

interface ToastProps {
  kind: ToastKind;
  title: string;
  message?: string;
  onClose?: () => void;
  action?: { label: string; onClick: () => void };
  style?: React.CSSProperties;
}

const palette: Record<ToastKind, { bg: string; soft: string }> = {
  success: { bg: 'var(--success)', soft: 'var(--success-soft)' },
  warning: { bg: 'var(--warning)', soft: 'var(--warning-soft)' },
  error:   { bg: 'var(--danger)',  soft: 'var(--danger-soft)' },
  info:    { bg: 'var(--info)',    soft: 'var(--info-soft)' },
};

const icons: Record<ToastKind, string> = {
  success: '✓',
  warning: '!',
  error:   '✕',
  info:    'i',
};

const Toast: React.FC<ToastProps> = ({ kind, title, message, onClose, action, style }) => {
  const p = palette[kind];
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 14px',
        borderRadius: 'var(--radius-md)',
        background: '#fff',
        border: '1px solid var(--border-1)',
        boxShadow: 'var(--shadow-lg)',
        maxWidth: 380,
        animation: 'cgToastIn 220ms var(--ease-standard)',
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 24, height: 24, borderRadius: '50%',
          background: p.bg, color: '#fff',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700, flexShrink: 0,
          marginTop: 1,
        }}
      >
        {icons[kind]}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--fg-1)', lineHeight: 1.35 }}>
          {title}
        </div>
        {message && (
          <div style={{ fontSize: 12.5, color: 'var(--fg-3)', marginTop: 2, lineHeight: 1.5, wordBreak: 'break-word' }}>
            {message}
          </div>
        )}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            style={{
              marginTop: 8,
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: p.bg,
              fontWeight: 600,
              fontSize: 12.5,
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
            }}
          >
            {action.label}
          </button>
        )}
      </div>
      {onClose && (
        <button
          aria-label="Dismiss"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--fg-3)',
            cursor: 'pointer',
            fontSize: 12,
            padding: 2,
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default Toast;
